import { cx } from "@/lib/cx";

/**
 * The parts a row is made of, shared by the grouped inset list
 * ({@link ../list/GroupedList}) and by the plain divided list (`ui/ListRow`).
 *
 * They live here rather than inside either component because the two lists are
 * *structurally* different — one is a card with separators inset to the text,
 * the other is a divided list on the page background — while the things inside
 * a row are identical. Before this, the tick was drawn twice with two different
 * fills and the disclosure chevron three times at three different sizes.
 */

/**
 * How tall a row is.
 *
 * The legacy density names remain source-compatible, but both resolve to the
 * one 40px row recipe and rise to 44px on a coarse pointer.
 */
export type ListDensity = "compact" | "comfortable";

/**
 * `min-h`, never `h`. The rung is a floor, not a ceiling: a row title clamps to
 * two lines below `sm` (`title-clamp`), and a fixed height would put the second
 * line under the row below it instead of growing the row.
 */
export const ROW_RECIPE = "row-height";

export const ROW_HEIGHT: Record<ListDensity, string> = {
  compact: ROW_RECIPE,
  comfortable: ROW_RECIPE,
};

/**
 * The leading gutter, in pixels, that a row's separator must start at.
 *
 * The separator in a grouped list begins at the **text**, so the inset is
 * whatever sits to the left of the title: the group's own 16px padding, plus a
 * 24px leading slot and its 12px gap when the row has one.
 */
export const SEPARATOR_INSET = { plain: 16, leading: 52 } as const;

/* -------------------------------------------------------------------------- */

/**
 * The tick.
 *
 * A **circle**, not a rounded square — this is the shape iOS uses for a
 * completable row and it is the single cheapest way to stop a checklist reading
 * as a web form. Decoration only: the button around it owns `role="checkbox"`
 * and the accessible name, so it is hidden from the accessibility tree.
 *
 * The fill is `bg-accent`, the **vivid** turquoise, and the glyph on it is
 * `text-accent-on` — the dark label solved for that fill (4.59 light / 8.67
 * dark). It was `bg-focus`, which still resolves and still passes, but which is
 * the dark muted teal the user called green. A tick is a mark, not text: it is
 * exactly the call site the accent split exists for.
 */
export function Tick({ checked, className }: { checked: boolean; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "busee-tick flex size-5 shrink-0 items-center justify-center rounded-control border-2",
        "transition-colors dur-fast",
        checked
          ? "border-ps1-gold bg-ps1-gold-fill text-ps1-gold-ink"
          : "border-[var(--ps1-tick-edge)] bg-[var(--ps1-tick)] text-transparent group-hover/row:border-ps1-gold",
        className,
      )}
    >
      <svg
        viewBox="0 0 14 14"
        aria-hidden
        fill="none"
        // The overshoot is the whole point of `dur-bounce` and this is the one
        // place in the app that earns it: a tick landing is the app's single
        // most-repeated moment of feedback.
        className={cx(
          "size-3 stroke-current transition-transform dur-bounce",
          checked ? "scale-100" : "scale-50",
        )}
      >
        <path d="M2.5 7.4l3 3 6-6.4" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/**
 * The disclosure chevron: 12×20, `text-faint`, right-aligned.
 *
 * It means "this row opens something". A row without one does not open
 * anything — it is not decoration, and `IOS-DESIGN.md` §5.4 rejects using it as
 * such. On the row whose detail is currently open it rotates to point down, so
 * the list marks the open row twice: the rail says *which*, the chevron says
 * *open*.
 */
export function DisclosureChevron({
  expanded = false,
  className,
}: {
  expanded?: boolean;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 12 20"
      aria-hidden="true"
      fill="none"
      className={cx(
        "h-5 w-3 shrink-0 stroke-current text-faint transition-transform dur-snappy",
        expanded && "rotate-90",
        className,
      )}
    >
      <path d="M3.5 5.5 8 10l-4.5 4.5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
